import { Link } from "react-router-dom";
import { Table } from "reactstrap";
import Loader from "../../../components/layout/Loader";
import NoDataFound from "../../../components/layout/NoDataFound";
import {
  COMPANY_DETAILS_ROUTE,
  LINKEDIN_FEED_REPORT_HEADER,
  LINKEDIN_FEED_REPORT_HEADER_GROUPS,
} from "../../../utils/constant";
import "./linkedin-feed-report.scss";

export interface LinkedinFeedReportRow {
  companyId: string;
  companyName: string;
  /** Job orders of this company inside the feed's top-165 ads. */
  jobOrders: number;
  /** Ads (advertised locations) of this company inside the feed's top-165. */
  jobAds: number;
  /** All active job orders of this company with at least one posted location (not capped). */
  totalJobOrders: number;
  /** All posted locations across those job orders (not capped). */
  totalJobAds: number;
}

// The shared TableComponent renders its own single-row <thead> and can't take
// a <colgroup>, grouped headers or per-column classes, so this report renders
// the table directly with the same "table" class (shared padding/hover styles
// still apply) plus its own two-band header, column widths and numeric
// alignment.
const [
  HEADER_INDEX,
  HEADER_COMPANY,
  HEADER_FEED_JOB_ORDERS,
  HEADER_FEED_JOB_ADS,
  HEADER_ALL_JOB_ORDERS,
  HEADER_ALL_JOB_ADS,
] = LINKEDIN_FEED_REPORT_HEADER;
const [GROUP_FEED, GROUP_ALL] = LINKEDIN_FEED_REPORT_HEADER_GROUPS;

const Listing = ({ loading, rows }: { loading: boolean; rows: LinkedinFeedReportRow[] }) => {
  if (loading) {
    return <Loader loaderClass="sm-height" />;
  }

  // Footer subtotals. "feed" = in-feed counts (jobOrders/jobAds); "inventory" =
  // the uncapped totals (totalJobOrders/totalJobAds).
  const feedJobOrdersSum = rows.reduce((sum, r) => sum + (r.jobOrders || 0), 0);
  const feedJobAdsSum = rows.reduce((sum, r) => sum + (r.jobAds || 0), 0);
  const inventoryJobOrdersSum = rows.reduce((sum, r) => sum + (r.totalJobOrders || 0), 0);
  const inventoryJobAdsSum = rows.reduce((sum, r) => sum + (r.totalJobAds || 0), 0);

  return (
    <div className="table-responsive lfr-table-wrap">
      <Table className="table lfr-table" hover>
        <colgroup>
          <col className="lfr-col-index" />
          <col />
          <col className="lfr-col-count" />
          <col className="lfr-col-count" />
          <col className="lfr-col-count" />
          <col className="lfr-col-count" />
        </colgroup>
        <thead>
          <tr className="lfr-head-groups">
            <th rowSpan={2} className="lfr-head-span">
              {HEADER_INDEX}
            </th>
            <th rowSpan={2} className="lfr-head-span">
              {HEADER_COMPANY}
            </th>
            <th colSpan={2} className="lfr-group lfr-group-feed">
              <span className="lfr-group__label">{GROUP_FEED.label}</span>
              <span className="lfr-group__hint">{GROUP_FEED.hint}</span>
            </th>
            <th colSpan={2} className="lfr-group lfr-group-all lfr-sep">
              <span className="lfr-group__label">{GROUP_ALL.label}</span>
              <span className="lfr-group__hint">{GROUP_ALL.hint}</span>
            </th>
          </tr>
          <tr className="lfr-head-cols">
            <th className="lfr-num">{HEADER_FEED_JOB_ORDERS}</th>
            <th className="lfr-num">{HEADER_FEED_JOB_ADS}</th>
            <th className="lfr-num lfr-sep">{HEADER_ALL_JOB_ORDERS}</th>
            <th className="lfr-num">{HEADER_ALL_JOB_ADS}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={LINKEDIN_FEED_REPORT_HEADER.length} className="empty-table-td">
                <NoDataFound text="No Data Found!" />
              </td>
            </tr>
          ) : (
            rows.map((item, index) => (
              <tr key={item.companyId} className="job-orders-tbody">
                <td className="lfr-index">{index + 1}</td>
                <td className="route-link lfr-company" title={item.companyName}>
                  <Link target="_blank" to={{ pathname: `${COMPANY_DETAILS_ROUTE}/${item.companyId}` }}>
                    {item.companyName}
                  </Link>
                </td>
                <td className="lfr-num">{item.jobOrders}</td>
                <td className="lfr-num">{item.jobAds}</td>
                <td className="lfr-num lfr-sep">{item.totalJobOrders}</td>
                <td className="lfr-num">{item.totalJobAds}</td>
              </tr>
            ))
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td></td>
              <td>Subtotal</td>
              <td className="lfr-num">{feedJobOrdersSum}</td>
              <td className="lfr-num">{feedJobAdsSum}</td>
              <td className="lfr-num lfr-sep">{inventoryJobOrdersSum}</td>
              <td className="lfr-num">{inventoryJobAdsSum}</td>
            </tr>
          </tfoot>
        )}
      </Table>
    </div>
  );
};

export default Listing;
